// components/Sources/SourceConfig.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Loader, Eye, EyeOff } from 'lucide-react';
import { sourcesApi } from '../../services/api';
import toast from 'react-hot-toast';

const SOURCE_FIELDS = {
  GCP: [
    { key: 'projectId',    label: 'Project ID',      type: 'text',     placeholder: 'my-gcp-project',                envVar: 'GCP_PROJECT_ID' },
    { key: 'keyFile',      label: 'Key File Path',   type: 'text',     placeholder: '/path/to/service-account.json', envVar: 'GCP_KEY_FILE' },
    { key: 'pollInterval', label: 'Poll Interval',   type: 'number',   placeholder: '30000',                         envVar: 'GCP_POLL_INTERVAL', suffix: 'ms' },
  ],
  AZURE: [
    { key: 'tenantId',     label: 'Tenant ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_TENANT_ID' },
    { key: 'clientId',     label: 'Client ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_CLIENT_ID' },
    { key: 'clientSecret', label: 'Client Secret',   type: 'password', placeholder: '••••••••',                             envVar: 'AZURE_CLIENT_SECRET' },
    { key: 'subscriptionId', label: 'Subscription ID', type: 'text',   placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_SUBSCRIPTION_ID' },
    { key: 'workspaceId',  label: 'Workspace ID',    type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_LOG_ANALYTICS_WORKSPACE_ID' },
    { key: 'resourceGroup', label: 'Resource Group', type: 'text',     placeholder: 'my-resource-group',                    envVar: 'AZURE_RESOURCE_GROUP' },
    { key: 'pollInterval', label: 'Poll Interval',   type: 'number',   placeholder: '30000',                                envVar: 'AZURE_POLL_INTERVAL', suffix: 'ms' },
  ],
  MONGODB: [
    { key: 'uri',          label: 'Connection URI',  type: 'password', placeholder: 'mongodb+srv://user:pass@cluster.mongodb.net/db', envVar: 'MONGODB_URI' },
    { key: 'dbName',       label: 'Database Name',   type: 'text',     placeholder: 'observability',                     envVar: 'MONGODB_DB_NAME' },
    { key: 'tailInterval', label: 'Tail Interval',   type: 'number',   placeholder: '10000',                             envVar: 'MONGO_TAIL_INTERVAL', suffix: 'ms' },
  ],
  DATABASE: [
    { key: 'host',         label: 'Host',            type: 'text',     placeholder: 'localhost',   envVar: 'POSTGRES_HOST' },
    { key: 'port',         label: 'Port',            type: 'number',   placeholder: '5432',        envVar: 'POSTGRES_PORT' },
    { key: 'user',         label: 'Username',        type: 'text',     placeholder: 'postgres',    envVar: 'POSTGRES_USER' },
    { key: 'password',     label: 'Password',        type: 'password', placeholder: '••••••••',    envVar: 'POSTGRES_PASSWORD' },
    { key: 'database',     label: 'Database',        type: 'text',     placeholder: 'prod_db',     envVar: 'POSTGRES_DB' },
    { key: 'pollInterval', label: 'Poll Interval',   type: 'number',   placeholder: '15000',       envVar: 'PG_TAIL_INTERVAL', suffix: 'ms' },
  ],
};

const SOURCE_DOCS = {
  GCP: 'Connects to Google Cloud Logging. Set GCP_PROJECT_ID and optionally GCP_KEY_FILE in your .env. If no key file is provided, Application Default Credentials (ADC) are used.',
  AZURE: 'Connects to Azure Monitor Log Analytics. Requires a Service Principal with Log Analytics Reader role on your workspace.',
  MONGODB: 'Tails MongoDB system.profile and change streams for slow queries and lock events. Requires profiling level ≥ 1 on the target database.',
  DATABASE: 'Polls pg_stat_activity and pg_stat_statements for slow queries, lock waits, and connection pool saturation. Requires pg_stat_statements extension.',
};

function FieldInput({ field, value, onChange }) {
  const [show, setShow] = useState(false);
  const isPassword = field.type === 'password';

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <label style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600 }}>{field.label}</label>
        <span style={{
          fontSize: 9, color: 'var(--text-3)', fontFamily: "'Space Mono', monospace",
          background: 'var(--bg-primary)', padding: '1px 5px', borderRadius: 3,
          border: '1px solid var(--border)',
        }}>
          env: {field.envVar}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type={isPassword && !show ? 'password' : 'text'}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={e => onChange(field.key, e.target.value)}
          style={{
            flex: 1, padding: '7px 10px', borderRadius: 6,
            background: 'var(--bg-primary)', border: '1px solid var(--border)',
            color: 'var(--text)', fontSize: 11,
            fontFamily: isPassword ? 'monospace' : 'inherit',
            outline: 'none',
          }}
        />
        {field.suffix && (
          <span style={{ fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{field.suffix}</span>
        )}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-3)', padding: 4,
            }}
          >
            {show ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        )}
      </div>
    </div>
  );
}

export function SourceConfig({ sourceType }) {
  const type = sourceType?.toUpperCase();
  const fields = SOURCE_FIELDS[type] || [];

  const [config, setConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setLoadingConfig(true);
    setTestResult(null);
    sourcesApi.getConfig(type)
      .then(cfg => setConfig(cfg || {}))
      .catch(() => setConfig({}))
      .finally(() => setLoadingConfig(false));
  }, [type]);

  function handleChange(key, value) {
    setConfig(c => ({ ...c, [key]: value }));
    setTestResult(null);
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await sourcesApi.test(type, config);
      setTestResult(result);
    } catch (err) {
      setTestResult({ connected: false, message: err?.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await sourcesApi.updateConfig(type, config);
      toast.success('Configuration saved');
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  }

  if (loadingConfig) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'var(--text-3)', fontSize: 12 }}>
        <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
        Loading configuration…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 16px 24px' }}>
      {/* Info banner */}
      <div style={{
        background: 'rgba(99,102,241,.08)',
        border: '1px solid rgba(99,102,241,.2)',
        borderRadius: 8, padding: '10px 14px',
        fontSize: 11, color: 'var(--text-2)', lineHeight: 1.6,
        marginBottom: 20,
      }}>
        {SOURCE_DOCS[type] || 'Configure the connection settings for this data source.'}
      </div>

      {/* Fields */}
      {fields.map(field => (
        <FieldInput
          key={field.key}
          field={field}
          value={config[field.key]}
          onChange={handleChange}
        />
      ))}

      {fields.length === 0 && (
        <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '12px 0' }}>
          No configuration fields available for this source type.
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px', borderRadius: 8, marginBottom: 16,
          fontSize: 11,
          background: testResult.connected
            ? (testResult.warning ? 'rgba(245,158,11,.08)' : 'rgba(34,197,94,.08)')
            : 'rgba(239,68,68,.08)',
          border: `1px solid ${testResult.connected
            ? (testResult.warning ? 'rgba(245,158,11,.25)' : 'rgba(34,197,94,.25)')
            : 'rgba(239,68,68,.25)'}`,
          color: testResult.connected
            ? (testResult.warning ? '#fbbf24' : '#4ade80')
            : '#f87171',
        }}>
          {testResult.connected
            ? (testResult.warning ? <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> : <CheckCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />)
            : <XCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          }
          {testResult.message}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleTest}
          disabled={testing || fields.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 7, fontSize: 12,
            border: '1px solid var(--border)',
            background: 'var(--bg-tertiary)',
            color: testing ? 'var(--text-3)' : 'var(--text)',
            cursor: testing ? 'wait' : 'pointer',
          }}
        >
          {testing
            ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : null
          }
          Test Connection
        </button>
        <button
          onClick={handleSave}
          disabled={saving || fields.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 7, fontSize: 12,
            border: 'none',
            background: saving ? 'var(--bg-tertiary)' : '#3b82f6',
            color: saving ? 'var(--text-3)' : 'white',
            cursor: saving ? 'wait' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : null}
          Save Configuration
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
